import { filterObjectKeys, getProperty } from '../components/utils'

const initialState = {
    data: null,

    errors: {},
    equipment_list_errors: {},
    errorModal: false,

    camera_makes: {
        video: null,
        still: null,
    },
    camera_models: {
        video: null,
        still: null,
    },
    lens_makes: null,
    lens_models: null,
    support_equipment_types: null,
    microphone_types: null,
    microphone_makes: null,
    audio_recorder_types: null,
    audio_recorder_makes: null,
    video_recorder_makes: null,
    wireless_system_types: {
        video: null,
        audio: null,
    },
    wireless_system_makes: {
        video: null,
        audio: null,
    },
    lighting_kit_types: {
        video: null,
        still: null,
    },
    lighting_kit_makes: {
        video: null,
        still: null,
    },
    monitor_types: null,
    monitor_makes: null,
    equipment_list_upload_progress: null,
    transportation_options: null,
    software_options: null,
}

function handleSubmittedItem(key, state, action) {
    const newItems = []

    let found = false

    // Go through each item in the current state and, if it matches the data being passed in,
    // replace it, otherwise, just add it as normal.
    if (state.data[key]) {
        for (let item of state.data[key]) {
            if (item.uuid === action.data.uuid) {
                item = action.data
                found = true
            }

            newItems.push(item)
        }
    }

    // If we didn't find the item in the current state, it's new, so add it to the end.
    if (! found) {
        newItems.push(action.data)
    }

    return {
        ...state,
        errors: {},
        data: {
            ...state.data,
            [key]: newItems,
        },
    }
}

/*
 * Handle submitted item but for an array of items.
 *
 * Just iterate through and call handleSubmittedItem for each one, constantly building on the state.
 */
function handleSubmittedItems(key, state, action) {
    return action.data.reduce(
        (state, action) => handleSubmittedItem(key, state, { data: action }),
        { ...state }
    )
}

function handleDeletedItem(keys, state, action) {
    const newData = {}

    // if we got passed just a single key as a string, coerce it to an array
    if (typeof keys === 'string') {
        keys = [ keys ]
    }

    for (const key of keys) {
        // take current list from current state and filter out the deleted one
        newData[key] = state.data[key].filter(
            (item) => (item.uuid !== action.data.uuid)
        )
    }

    return {
        ...state,
        data: {
            ...state.data,
            ...newData,
        },
    }
}

function handleSubmittedComputer(state, action) {
    const newState = handleSubmittedItem('computers', state, action)

    if (newState.data.laptop && newState.data.laptop.uuid === action.data.uuid) {
        newState.data.laptop = {
            ...newState.data.laptop,
            computer_type: action.data.computer_type,
            computer_type_verbal: action.data.computer_type_verbal,
            make: action.data.make,
            make_verbal: action.data.make_verbal,
            model: action.data.model,
            year: action.data.year,
            usb3: action.data.usb3,
            thunderbolt: action.data.thunderbolt,
            additional_details: action.data.additional_details,
        }
    }

    if (newState.data.desktop && newState.data.desktop.uuid === action.data.uuid) {
        newState.data.desktop = {
            ...newState.data.desktop,
            computer_type: action.data.computer_type,
            computer_type_verbal: action.data.computer_type_verbal,
            make: action.data.make,
            make_verbal: action.data.make_verbal,
            model: action.data.model,
            year: action.data.year,
            usb3: action.data.usb3,
            thunderbolt: action.data.thunderbolt,
            additional_details: action.data.additional_details,
        }
    }

    return newState
}

function handleDeletedComputer(state, action) {
    const newState = handleDeletedItem('computers', state, action)

    if (newState.data.laptop && newState.data.laptop.uuid === action.data.uuid) {
        newState.data.laptop = null
        newState.data.own_laptop = false
    }

    if (newState.data.desktop && newState.data.desktop.uuid === action.data.uuid) {
        newState.data.desktop = null
        newState.data.own_desktop = false
    }

    return newState
}

export default function gear(state = initialState, action) {
    switch (action.type) {
        case 'SIGN_OUT':
            return initialState

        // General loading of a crewmember's gear
        case 'LOAD_GEAR_SUCCESS':
            return {
                ...state,
                data: action.data,
            }
        case 'LOAD_GEAR_ERROR':
            return {
                ...state,
                data: null,
            }

        // General patching of a crewmember's gear
        case 'PATCH_GEAR_SUCCESS':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                },
                errorModal: false,

                // If we had a list of errors in the state, here we remove from that list any that
                // were associated to the fields that we successfully patched (as they are no longer
                // in an error condition).
                errors: filterObjectKeys(state.errors || {}, Object.keys(action.data), 'exclude'),
            }

        case 'PATCH_GEAR_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: false,
            }

        // General posting of a crewmember's gear
        case 'POST_GEAR_SUCCESS':
            return {
                ...state,
                data: action.data,
                errors: {},
            }

        case 'POST_GEAR_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: Date.now(),
            }

        case 'COMMIT_GEAR_SUCCESS':
            // nothing special here
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                },
                errors: {},
            }

        case 'COMMIT_GEAR_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: Date.now(),
            }

        // Get video camera makes
        case 'LOAD_VIDEO_CAMERA_MAKES_SUCCESS':
            return {
                ...state,
                camera_makes: {
                    ...state.camera_makes,
                    video: action.data,
                },
            }
        case 'LOAD_VIDEO_CAMERA_MAKES_ERROR':
            return {
                ...state,
                camera_makes: {
                    ...state.camera_makes,
                    video: null,
                },
            }

        // Get video camera models
        case 'LOAD_VIDEO_CAMERA_MODELS_SUCCESS':
            return {
                ...state,
                camera_models: {
                    ...state.camera_makes,
                    video: action.data,
                },
            }
        case 'LOAD_VIDEO_CAMERA_MODELS_ERROR':
            return {
                ...state,
                camera_models: {
                    ...state.camera_makes,
                    video: null,
                },
            }

        // Add/update video camera
        case 'SUBMIT_VIDEO_CAMERA_SUCCESS':
            return handleSubmittedItem('video_cameras', state, action)

        case 'SUBMIT_VIDEO_CAMERA_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Delete video camera
        case 'DELETE_VIDEO_CAMERA_SUCCESS':
            return handleDeletedItem([ 'video_cameras', 'still_cameras' ], state, action)

        // Get still camera makes
        case 'LOAD_STILL_CAMERA_MAKES_SUCCESS':
            return {
                ...state,
                camera_makes: {
                    ...state.camera_makes,
                    still: action.data,
                },
            }
        case 'LOAD_STILL_CAMERA_MAKES_ERROR':
            return {
                ...state,
                camera_makes: {
                    ...state.camera_makes,
                    still: null,
                },
            }

        // Get still camera models
        case 'LOAD_STILL_CAMERA_MODELS_SUCCESS':
            return {
                ...state,
                camera_models: {
                    ...state.camera_makes,
                    still: action.data,
                },
            }
        case 'LOAD_STILL_CAMERA_MODELS_ERROR':
            return {
                ...state,
                camera_models: {
                    ...state.camera_makes,
                    still: null,
                },
            }

        // Add/update still camera
        case 'SUBMIT_STILL_CAMERA_SUCCESS':
            return handleSubmittedItem('still_cameras', state, action)

        case 'SUBMIT_STILL_CAMERA_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Delete still camera
        case 'DELETE_STILL_CAMERA_SUCCESS':
            return handleDeletedItem([ 'video_cameras', 'still_cameras' ], state, action)

        // Get lens makes
        case 'LOAD_LENS_MAKES_SUCCESS':
            return {
                ...state,
                lens_makes: action.data,
            }
        case 'LOAD_LENS_MAKES_ERROR':
            return {
                ...state,
                lens_makes: null,
            }

        // Get lens models
        case 'LOAD_LENS_MODELS_SUCCESS':
            return {
                ...state,
                lens_models: action.data,
            }
        case 'LOAD_LENS_MODELS_ERROR':
            return {
                ...state,
                lens_models: null,
            }

        // Add/update lens
        case 'SUBMIT_LENS_SUCCESS':
            return handleSubmittedItem('lenses', state, action)

        case 'SUBMIT_LENS_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Add lenses
        case 'SUBMIT_LENSES_SUCCESS':
            return handleSubmittedItems('lenses', state, action)

        case 'SUBMIT_LENSES_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Delete lens
        case 'DELETE_LENS_SUCCESS':
            return handleDeletedItem('lenses', state, action)

        // Get support equipment types
        case 'LOAD_SUPPORT_EQUIPMENT_TYPES_SUCCESS':
            return {
                ...state,
                support_equipment_types: action.data,
            }
        case 'LOAD_SUPPORT_EQUIPMENT_TYPES_ERROR':
            return {
                ...state,
                support_equipment_types: null,
            }

        // Add/update item of support equipment
        case 'SUBMIT_SUPPORT_EQUIPMENT_SUCCESS':
            return handleSubmittedItem('support_equipment', state, action)

        case 'SUBMIT_SUPPORT_EQUIPMENT_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Delete item of support equiment
        case 'DELETE_SUPPORT_EQUIPMENT_SUCCESS':
            return handleDeletedItem('support_equipment', state, action)

        // Get microphone types
        case 'LOAD_MICROPHONE_TYPES_SUCCESS':
            return {
                ...state,
                microphone_types: action.data,
            }
        case 'LOAD_MICROPHONE_TYPES_ERROR':
            return {
                ...state,
                microphone_types: null,
            }

        // Get microphone makes
        case 'LOAD_MICROPHONE_MAKES_SUCCESS':
            return {
                ...state,
                microphone_makes: action.data,
            }
        case 'LOAD_MICROPHONE_MAKES_ERROR':
            return {
                ...state,
                microphone_makes: null,
            }

        // Add/update microphone
        case 'SUBMIT_MICROPHONE_SUCCESS':
            return handleSubmittedItem('microphones', state, action)

        case 'SUBMIT_MICROPHONE_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a microphone
        case 'DELETE_MICROPHONE_SUCCESS':
            return handleDeletedItem('microphones', state, action)

        // Get audio recorder/mixer makes
        case 'LOAD_AUDIO_RECORDER_TYPES_SUCCESS':
            return {
                ...state,
                audio_recorder_types: action.data,
            }
        case 'LOAD_AUDIO_RECORDER_TYPES_ERROR':
            return {
                ...state,
                audio_recorder_types: null,
            }

        // Get audio recorder/mixer makes
        case 'LOAD_AUDIO_RECORDER_MAKES_SUCCESS':
            return {
                ...state,
                audio_recorder_makes: action.data,
            }
        case 'LOAD_AUDIO_RECORDER_MAKES_ERROR':
            return {
                ...state,
                audio_recorder_makes: null,
            }

        // Add/update audio recorder/mixer
        case 'SUBMIT_AUDIO_RECORDER_SUCCESS':
            return handleSubmittedItem('audio_recorders', state, action)

        case 'SUBMIT_AUDIO_RECORDER_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of an audio recorder/mixer
        case 'DELETE_AUDIO_RECORDER_SUCCESS':
            return handleDeletedItem('audio_recorders', state, action)

        // Get video recorder makes
        case 'LOAD_VIDEO_RECORDER_MAKES_SUCCESS':
            return {
                ...state,
                video_recorder_makes: action.data,
            }
        case 'LOAD_VIDEO_RECORDER_MAKES_ERROR':
            return {
                ...state,
                video_recorder_makes: null,
            }

        // Add/update video recorder
        case 'SUBMIT_VIDEO_RECORDER_SUCCESS':
            return handleSubmittedItem('video_recorders', state, action)

        case 'SUBMIT_VIDEO_RECORDER_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a video recorder
        case 'DELETE_VIDEO_RECORDER_SUCCESS':
            return handleDeletedItem('video_recorders', state, action)

        // Get wireless audio system types
        case 'LOAD_WIRELESS_AUDIO_SYSTEM_TYPES_SUCCESS':
            return {
                ...state,
                wireless_system_types: {
                    ...state.wireless_system_types,
                    audio: action.data,
                },
            }
        case 'LOAD_WIRELESS_AUDIO_SYSTEM_TYPES_ERROR':
            return {
                ...state,
                wireless_system_types: {
                    ...state.wireless_system_types,
                    audio: null,
                },
            }

        // Get wireless audio system makes
        case 'LOAD_WIRELESS_AUDIO_SYSTEM_MAKES_SUCCESS':
            return {
                ...state,
                wireless_system_makes: {
                    ...state.wireless_system_makes,
                    audio: action.data,
                },
            }
        case 'LOAD_WIRELESS_AUDIO_SYSTEM_MAKES_ERROR':
            return {
                ...state,
                wireless_system_makes: {
                    ...state.wireless_system_makes,
                    audio: null,
                },
            }

        // Add/update wireless audio system
        case 'SUBMIT_WIRELESS_AUDIO_SYSTEM_SUCCESS':
            return handleSubmittedItem('audio_wireless_systems', state, action)

        case 'SUBMIT_WIRELESS_AUDIO_SYSTEM_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a wireless audio system
        case 'DELETE_WIRELESS_AUDIO_SYSTEM_SUCCESS':
            return handleDeletedItem('audio_wireless_systems', state, action)

        // Get wireless video system types
        case 'LOAD_WIRELESS_VIDEO_SYSTEM_TYPES_SUCCESS':
            return {
                ...state,
                wireless_system_types: {
                    ...state.wireless_system_types,
                    video: action.data,
                },
            }
        case 'LOAD_WIRELESS_VIDEO_SYSTEM_TYPES_ERROR':
            return {
                ...state,
                wireless_system_types: {
                    ...state.wireless_system_types,
                    video: null,
                },
            }

        // Get wireless video system makes
        case 'LOAD_WIRELESS_VIDEO_SYSTEM_MAKES_SUCCESS':
            return {
                ...state,
                wireless_system_makes: {
                    ...state.wireless_system_makes,
                    video: action.data,
                },
            }
        case 'LOAD_WIRELESS_VIDEO_SYSTEM_MAKES_ERROR':
            return {
                ...state,
                wireless_system_makes: {
                    ...state.wireless_system_makes,
                    video: null,
                },
            }

        // Add/update wireless video system
        case 'SUBMIT_WIRELESS_VIDEO_SYSTEM_SUCCESS':
            return handleSubmittedItem('video_wireless_systems', state, action)

        case 'SUBMIT_WIRELESS_VIDEO_SYSTEM_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a wireless video system
        case 'DELETE_WIRELESS_VIDEO_SYSTEM_SUCCESS':
            return handleDeletedItem('video_wireless_systems', state, action)

        // Get video lighting kit types
        case 'LOAD_VIDEO_LIGHTING_KIT_TYPES_SUCCESS':
            return {
                ...state,
                lighting_kit_types: {
                    ...state.lighting_kit_types,
                    video: action.data,
                },
            }
        case 'LOAD_VIDEO_LIGHTING_KIT_TYPES_ERROR':
            return {
                ...state,
                lighting_kit_types: {
                    ...state.lighting_kit_types,
                    video: null,
                },
            }

        // Get video lighting kit makes
        case 'LOAD_VIDEO_LIGHTING_KIT_MAKES_SUCCESS':
            return {
                ...state,
                lighting_kit_makes: {
                    ...state.lighting_kit_makes,
                    video: action.data,
                },
            }
        case 'LOAD_VIDEO_LIGHTING_KIT_MAKES_ERROR':
            return {
                ...state,
                lighting_kit_makes: {
                    ...state.lighting_kit_makes,
                    video: null,
                },
            }

        // Add/update video lighting kit
        case 'SUBMIT_VIDEO_LIGHTING_KIT_SUCCESS':
            return handleSubmittedItem('video_lighting_kits', state, action)

        case 'SUBMIT_VIDEO_LIGHTING_KIT_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a video lighting kit
        case 'DELETE_VIDEO_LIGHTING_KIT_SUCCESS':
            return handleDeletedItem('video_lighting_kits', state, action)

        // Get still lighting kit types
        case 'LOAD_STILL_LIGHTING_KIT_TYPES_SUCCESS':
            return {
                ...state,
                lighting_kit_types: {
                    ...state.lighting_kit_types,
                    still: action.data,
                },
            }
        case 'LOAD_STILL_LIGHTING_KIT_TYPES_ERROR':
            return {
                ...state,
                lighting_kit_types: {
                    ...state.lighting_kit_types,
                    still: null,
                },
            }

        // Get still lighting kit makes
        case 'LOAD_STILL_LIGHTING_KIT_MAKES_SUCCESS':
            return {
                ...state,
                lighting_kit_makes: {
                    ...state.lighting_kit_makes,
                    still: action.data,
                },
            }
        case 'LOAD_STILL_LIGHTING_KIT_MAKES_ERROR':
            return {
                ...state,
                lighting_kit_makes: {
                    ...state.lighting_kit_makes,
                    still: null,
                },
            }

        // Add/update still lighting kit
        case 'SUBMIT_STILL_LIGHTING_KIT_SUCCESS':
            return handleSubmittedItem('still_lighting_kits', state, action)

        case 'SUBMIT_STILL_LIGHTING_KIT_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a still lighting kit
        case 'DELETE_AUDIO_LIGHTING_KIT_SUCCESS':
            return handleDeletedItem('still_lighting_kits', state, action)

        // Get lighting kit types
        case 'LOAD_MONITOR_TYPES_SUCCESS':
            return {
                ...state,
                monitor_types: action.data,
            }
        case 'LOAD_MONITOR_TYPES_ERROR':
            return {
                ...state,
                monitor_types: null,
            }

        // Get monitor makes
        case 'LOAD_MONITOR_MAKES_SUCCESS':
            return {
                ...state,
                monitor_makes: action.data,
            }
        case 'LOAD_MONITOR_MAKES_ERROR':
            return {
                ...state,
                monitor_makes: null,
            }

        // Add/update monitor
        case 'SUBMIT_MONITOR_SUCCESS':
            return handleSubmittedItem('monitors', state, action)

        case 'SUBMIT_MONITOR_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a monitor
        case 'DELETE_MONITOR_SUCCESS':
            return handleDeletedItem('monitors', state, action)

        // Add/update computer
        case 'SUBMIT_COMPUTER_SUCCESS':
            return handleSubmittedComputer(state, action)

        case 'SUBMIT_COMPUTER_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of a computer
        case 'DELETE_COMPUTER_SUCCESS':
            return handleDeletedComputer(state, action)

        // Add/update additional gear
        case 'SUBMIT_ADDITIONAL_GEAR_SUCCESS':
            return handleSubmittedItem('additional_gear', state, action)

        case 'SUBMIT_ADDITIONAL_GEAR_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of additional gear
        case 'DELETE_ADDITIONAL_GEAR_SUCCESS':
            return handleDeletedItem('additional_gear', state, action)

        // Add/update drone
        case 'SUBMIT_DRONE_SUCCESS':
            return handleSubmittedItem('drones', state, action)

        case 'SUBMIT_DRONE_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of drone
        case 'DELETE_DRONE_SUCCESS':
            return handleDeletedItem('drones', state, action)

        // Add/update DIT software package
        case 'SUBMIT_DIT_SOFTWARE_SUCCESS':
            return handleSubmittedItem('dit_software', state, action)

        case 'SUBMIT_DIT_SOFTWARE_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of DIT software package
        case 'DELETE_DIT_SOFTWARE_SUCCESS':
            return handleDeletedItem('dit_software', state, action)

        // Upload of equipment list
        case 'UPLOAD_EQUIPMENT_LIST_SUCCESS':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                },
                equipment_list_errors: {},
                equipment_list_upload_progress: null,
            }
        case 'UPLOAD_EQUIPMENT_LIST_ERROR':
            return {
                ...state,
                equipment_list_errors: action.errors || {},
                equipment_list_upload_progress: null,
            }
        case 'UPLOAD_EQUIPMENT_LIST_PROGRESS':
            return {
                ...state,
                equipment_list_upload_progress: action.percent,
            }

        // Get transporation options
        case 'LOAD_TRANSPORTATION_OPTIONS_SUCCESS':
            return {
                ...state,
                transportation_options: action.data,
            }
        case 'LOAD_TRANSPORTATION_OPTIONS_ERROR':
            return {
                ...state,
                transportation_options: null,
            }

        // Get software options
        case 'LOAD_SOFTWARE_OPTIONS_SUCCESS':
            // split the loaded options into the respective groupings by type (fully dynamic)
            const software_options = {}

            for (const item of action.data) {
                for (const key in item) {
                    if (key.startsWith('type_') && (item[key] === true)) {
                        // get existing options for this type (or create an empty array if none exist)
                        // eslint-disable-next-line no-magic-numbers
                        const options = getProperty(software_options, key.slice(5), [])

                        // add this item
                        options.push(item)

                        // finally set the list as the new option list
                        // eslint-disable-next-line no-magic-numbers
                        software_options[key.slice(5)] = options
                    }
                }
            }

            return {
                ...state,
                software_options,
            }
        case 'LOAD_SOFTWARE_OPTIONS_ERROR':
            return {
                ...state,
                software_options: null,
            }

        // Add/update additional software
        case 'SUBMIT_ADDITIONAL_SOFTWARE_SUCCESS':
            return handleSubmittedItem('additional_software', state, action)

        case 'SUBMIT_ADDITIONAL_SOFTWARE_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of additional software
        case 'DELETE_ADDITIONAL_SOFTWARE_SUCCESS':
            return handleDeletedItem('additional_software', state, action)

        // Add/update additional hardware
        case 'SUBMIT_ADDITIONAL_HARDWARE_SUCCESS':
            return handleSubmittedItem('additional_hardware', state, action)

        case 'SUBMIT_ADDITIONAL_HARDWARE_ERROR':
            return {
                ...state,
                errors: action.errors || {},
                errorModal: action.errorModal || false,
            }

        // Deletion of additional hardware
        case 'DELETE_ADDITIONAL_HARDWARE_SUCCESS':
            return handleDeletedItem('additional_hardware', state, action)

        default:
            return state
    }
}



// WEBPACK FOOTER //
// ./app/reducers/gear.js